/**
 * Operations — what is in flight, what is stuck, what failed.
 *
 * THE ONE SCREEN SOMEBODY READS AT 2AM. Everything on it is ordered by how
 * urgent it is: needs-review first (a person is blocked), then in flight,
 * then recent failures. Elapsed time carries the alert colour because "how
 * long has it been like this" is the question, not "what state is it in".
 *
 * NO ORGS OR MEMBERS HERE. The Jinja page this replaces also created clubs
 * and added people to them, with no reason recorded and no audit row. Manage
 * Clubs and Manage Priori Staff do that now, through an API that refuses a
 * change without a reason.
 *
 * ACTIONS ARE PLATFORM-ADMIN. Staff read this page; killing a pod is a
 * different question from looking at one. Non-admins see the tables without
 * the buttons rather than buttons that 404.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import type { Session } from '../lib/api'
import { ApiError, api } from '../lib/api'
import { secsToField } from '../components/MatchMarks'
import { pathSeg } from '../lib/urls'
import './admin.css'
import './operations.css'

type Calibration = { n_samples?: number; verdict?: string } | null
type Job = {
  id: string
  game_code: string | null
  org_id: number | null
  uploader: string | null
  external: boolean
  status: string
  stage: string
  stage_detail: string | null
  stage_elapsed_s: number | null
  alert: 'ok' | 'warn' | 'bad'
  pod_id: string | null
  uploaded_at: string | null
  error: string | null
  calibration?: Calibration
  review_url?: string
}
type Payload = { active: Job[]; failed: Job[]; now: string }

/** One configuration check. `severity` decides whether a red entry means
 *  the pipeline is broken or merely that a known gap is still open. */
type Check = {
  name: string
  ok: boolean
  severity: 'required' | 'advisory'
  detail: string
}
type Health = { checks: Check[]; ok: boolean }

/** An upload from any of the three doors: the React upload page, the SDK
 *  tracking intake, and the lead portal. */
type Upload = {
  source: 'video' | 'tracking' | 'lead'
  id: string
  game_code: string | null
  org: string | null
  status: string | null
  uploaded_at: string | null
  filename: string | null
  size_mb: number | null
  error: string | null
  calibration_url: string | null
}

const REFRESH_MS = 30_000

function elapsed(s: number | null): string {
  if (s === null || s === undefined) return '—'
  if (s < 90) return `${s}s`
  if (s < 5400) return `${Math.round(s / 60)}m`
  return `${(s / 3600).toFixed(1)}h`
}

/** Each action, with the sentence the operator has to agree to. The wording is
 *  carried over verbatim from the page this replaces — it explains what the
 *  action actually does to the chunks, which is the part people get wrong. */
const CONFIRM: Record<string, (j: Job) => string> = {
  relaunch: (j) => `Kill pod ${j.pod_id ?? ''} and relaunch on a fresh host?`,
  fail: (j) => `Mark ${j.game_code} FAILED and kill its pods (the chunk `
    + `fleet and finalize pod too)?`,
  requeue: (j) => `Requeue ${j.game_code}? This re-runs the CALIBRATE leg (then `
    + `review and approve again). For chunk-phase failures use retry chunks instead.`,
  'retry-chunks': (j) => `Retry the failed chunks of ${j.game_code}? Completed `
    + `chunks are kept (R2 parquets re-adopted); only non-completed chunks relaunch.`,
  'resurrect-finalize': (j) => `Resurrect the FINALIZE for ${j.game_code}? `
    + `(requires all chunks completed)`,
}

type PodRow = {
  id: string
  name: string | null
  status: string | null
  started_at: string | null
  category: 'stuck' | 'orphan' | 'finished' | 'active' | 'foreign'
  why: string
  jobs: { id: string; status: string; game_code: string | null }[]
}
type PodsPayload = {
  configured: boolean
  env_name?: string | null
  stuck_hours?: number
  /** the categories the server will delete — served, never copied here */
  deletable?: string[]
  pods: PodRow[]
}

/** Every RunPod pod on the account, and a Delete for the ones no job needs:
 *  no job records it (orphan) or its job is done (finished). A stuck pod's
 *  job is still live — its In-flight row's Fail stops it (and all its
 *  pods). Another environment's pods are listed, never deletable here. */
export function RunPodPods({ mayAct }: { mayAct: boolean }) {
  const [data, setData] = useState<PodsPayload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const load = useCallback(() => {
    setError(null)
    api<PodsPayload>('/api/v1/ops/pods')
      .then(setData)
      .catch((e: unknown) =>
        setError(e instanceof ApiError ? e.message : String(e)))
  }, [])
  useEffect(() => { load() }, [load])

  const del = async (p: PodRow) => {
    const reason = window.prompt(
      `Delete ${p.category} pod ${p.id}?\n${p.why}\n\nReason (required):`)
    if (!reason?.trim()) return
    setMsg(null)
    try {
      await api(`/api/v1/ops/pods/${encodeURIComponent(p.id)}/delete`,
                { method: 'POST', body: JSON.stringify({ reason }) })
      setMsg(`Deleted pod ${p.id}.`)
    } catch (e: unknown) {
      setMsg(`Not deleted: ${e instanceof ApiError ? e.message : String(e)}`)
    }
    load()
  }

  return (
    <>
      <h2 className="ops-h2">RunPod pods</h2>
      {error && <p className="error">{error}</p>}
      {data && !data.configured && (
        <p className="muted">RunPod is not enabled in this environment.</p>
      )}
      {msg && <p className="muted">{msg}</p>}
      {data?.configured && (
        <table className="adm-table">
          <thead><tr><th>Pod</th><th>Status</th><th>Started</th>
            <th>State</th><th>Job</th><th /></tr></thead>
          <tbody>
            {data.pods.map((p) => (
              <tr key={p.id}>
                <td className="t-data">{p.id}<div className="muted">{p.name ?? '—'}</div></td>
                <td>{p.status ?? '—'}</td>
                <td className="t-data">{p.started_at ?? '—'}</td>
                <td title={p.why}><strong>{p.category}</strong>
                  <div className="muted">{p.why}</div></td>
                <td>{p.jobs.map((j) => `${j.game_code ?? j.id} (${j.status})`).join(', ') || '—'}</td>
                <td className="ops-actions">
                  {mayAct && (data.deletable ?? []).includes(p.category) && (
                    <button className="adm-btn" onClick={() => del(p)}>
                      Delete pod
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {data.pods.length === 0 && (
              <tr><td colSpan={6} className="adm-empty">No pods on the account.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </>
  )
}

type Timing = { kickoff_s?: number; h1_end_s?: number; h2_start_s?: number; game_end_s?: number }
type RerunGame = {
  game_id: number
  game_code: string
  match_date: string | null
  org_id: number
  org_code: string
  org_name: string
}
type RerunPlan = {
  game_code: string
  job_id: string
  chunks: number
  game_timing: Timing
  home_team_start_direction: string | null
}

function mmss(s: number | undefined): string {
  return s === undefined ? '—' : secsToField(s)
}

/** Re-run a finished game's FINALIZE with its current halftime marks.
 *
 *  The finalize splits the match into halves and decides which team is
 *  which; given no halftime it guesses, and a wrong guess puts players on
 *  the wrong team (CALGARY@OTTAWA, 2026-10-14). The GPU chunks are reused,
 *  so this is one pod, not the match. Check first: the server says what it
 *  will use, or why it cannot, before anything starts. */
export function RerunFinalize({ onDone }: {
  onDone: (t: { ok: boolean; text: string }) => void
}) {
  const [games, setGames] = useState<RerunGame[] | null>(null)
  const [clubId, setClubId] = useState('')
  const [gameId, setGameId] = useState('')
  const [plan, setPlan] = useState<RerunPlan | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const path = `/api/v1/ops/games/${encodeURIComponent(gameId)}/rerun-finalize`

  useEffect(() => {
    api<{ games: RerunGame[] }>('/api/v1/ops/rerun-finalize/games')
      .then((d) => setGames(d.games))
      .catch(() => setGames([]))
  }, [])
  // one entry per club, in the server's order (by club name)
  const clubs = (games ?? []).filter((g, i, all) =>
    all.findIndex((x) => x.org_id === g.org_id) === i)
  const matches = (games ?? []).filter((g) => String(g.org_id) === clubId)
  const pickClub = (v: string) => {
    setClubId(v); setGameId(''); setPlan(null); setRefusal(null)
  }
  const pickGame = (v: string) => {
    setGameId(v); setPlan(null); setRefusal(null)
  }

  const check = async () => {
    setPlan(null); setRefusal(null); setBusy(true)
    try {
      setPlan(await api<RerunPlan>(path))
    } catch (e) {
      setRefusal(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const start = async () => {
    if (!plan) return
    const reason = window.prompt(
      `Re-run the finalize of ${plan.game_code} with halftime `
      + `${mmss(plan.game_timing.h1_end_s)}–${mmss(plan.game_timing.h2_start_s)}? `
      + `The game keeps its current data until the new result lands; it then `
      + `reads the new run, so rebuild its layer stack from L1. Reason (required):`)
    if (reason === null) return
    if (!reason.trim()) {
      onDone({ ok: false, text: 'say why: a re-run replaces what the club sees' })
      return
    }
    setBusy(true)
    try {
      const r = await api<{ job_id: string }>(path, {
        method: 'POST', body: JSON.stringify({ reason }) })
      onDone({ ok: true, text: `finalize re-run started: job ${r.job_id.slice(0, 8)}` })
      setPlan(null); setGameId('')
    } catch (e) {
      onDone({ ok: false, text: e instanceof ApiError ? e.message : String(e) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <section aria-label="Re-run finalize">
      <h2 className="ops-h2">Re-run finalize</h2>
      <p className="page-sub">
        For a finished game whose teams or halves came out wrong: re-runs
        clustering and team assignment with the game&apos;s current halftime
        marks, reusing its tracked chunks. Set the marks first.
      </p>
      <div className="adm-bar">
        <label className="adm-field">
          <span className="t-label">Club</span>
          <select className="adm-select" value={clubId} aria-label="Club"
                  onChange={(e) => pickClub(e.target.value)}>
            <option value="" disabled>
              {games === null ? 'Loading…' : clubs.length ? 'Pick a club' : 'No games to re-run'}
            </option>
            {clubs.map((c) => (
              <option key={c.org_id} value={String(c.org_id)}>{c.org_name}</option>
            ))}
          </select>
        </label>
        <label className="adm-field">
          <span className="t-label">Match</span>
          <select className="adm-select" value={gameId} aria-label="Match"
                  disabled={!clubId} onChange={(e) => pickGame(e.target.value)}>
            <option value="" disabled>Pick a match</option>
            {matches.map((g) => (
              <option key={g.game_id} value={String(g.game_id)}>
                {g.game_code}{g.match_date ? ` · ${g.match_date}` : ''} · #{g.game_id}
              </option>
            ))}
          </select>
        </label>
        <button className="adm-btn adm-btn--quiet ops-btn"
                disabled={busy || !gameId} onClick={check}>Check</button>
      </div>
      {refusal && <p className="ops-err">{refusal}</p>}
      {plan && (
        <table className="adm-table">
          <thead><tr><th>Game</th><th>Job re-run</th><th className="adm-num">Chunks</th>
            <th>Kickoff</th><th>Halftime</th><th>Full time</th><th>Home starts</th><th /></tr></thead>
          <tbody>
            <tr>
              <td className="adm-primary">{plan.game_code}</td>
              <td className="t-data">{plan.job_id.slice(0, 8)}</td>
              <td className="adm-num">{plan.chunks}</td>
              <td className="t-data">{mmss(plan.game_timing.kickoff_s)}</td>
              <td className="t-data">
                {mmss(plan.game_timing.h1_end_s)}–{mmss(plan.game_timing.h2_start_s)}
              </td>
              <td className="t-data">{mmss(plan.game_timing.game_end_s)}</td>
              <td className="t-data">{plan.home_team_start_direction ?? '—'}</td>
              <td className="ops-actions">
                <button className="adm-btn adm-btn--danger ops-btn" disabled={busy}
                        onClick={start}>Re-run finalize</button>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </section>
  )
}

type RederivePlan = {
  game_code: string
  job_id: string
  results_prefix: string
  game_timing: Timing
}

/** Re-derive a finished game's TRACKING from its final parquet with its
 *  current halftime marks: TRDAAS's adapter as a layer-stack build on its
 *  own Cloud Run Job, no GPU (services/rederive_rerun). Replaces the job's tracking in
 *  place; the first re-derive keeps the original beside it. For marks set
 *  after the run — the cheaper path than Re-run finalize when the teams
 *  came out right. */
export function RerunRederive({ onDone }: {
  onDone: (t: { ok: boolean; text: string }) => void
}) {
  const [games, setGames] = useState<RerunGame[] | null>(null)
  const [clubId, setClubId] = useState('')
  const [gameId, setGameId] = useState('')
  const [plan, setPlan] = useState<RederivePlan | null>(null)
  const [refusal, setRefusal] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const path = `/api/v1/ops/games/${encodeURIComponent(gameId)}/rederive`

  useEffect(() => {
    api<{ games: RerunGame[] }>('/api/v1/ops/rederive/games')
      .then((d) => setGames(d.games))
      .catch(() => setGames([]))
  }, [])
  const clubs = (games ?? []).filter((g, i, all) =>
    all.findIndex((x) => x.org_id === g.org_id) === i)
  const matches = (games ?? []).filter((g) => String(g.org_id) === clubId)

  const check = async () => {
    setPlan(null); setRefusal(null); setBusy(true)
    try {
      setPlan(await api<RederivePlan>(path))
    } catch (e) {
      setRefusal(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const start = async () => {
    if (!plan) return
    const reason = window.prompt(
      `Re-derive the tracking of ${plan.game_code} with halftime `
      + `${mmss(plan.game_timing.h1_end_s)}–${mmss(plan.game_timing.h2_start_s)}? `
      + `This replaces the tracking the club sees once the build has run; `
      + `then ingest the tracking again on the layer stack page. Reason (required):`)
    if (reason === null) return
    if (!reason.trim()) {
      onDone({ ok: false, text: 'say why: a re-derive replaces what the club sees' })
      return
    }
    setBusy(true)
    try {
      const r = await api<{ batch_id: string }>(path, {
        method: 'POST', body: JSON.stringify({ reason }) })
      onDone({ ok: true, text: `re-derive queued: build ${r.batch_id.slice(0, 8)}` })
      setPlan(null); setGameId('')
    } catch (e) {
      onDone({ ok: false, text: e instanceof ApiError ? e.message : String(e) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <section aria-label="Re-derive tracking">
      <h2 className="ops-h2">Re-derive tracking</h2>
      <p className="page-sub">
        For a finished game whose halftime marks changed after it ran:
        rebuilds its tracking from the finalize&apos;s output with the
        game&apos;s current marks, without the GPU. If the teams themselves
        came out wrong, use Re-run finalize.
      </p>
      <div className="adm-bar">
        <label className="adm-field">
          <span className="t-label">Club</span>
          <select className="adm-select" value={clubId} aria-label="Club"
                  onChange={(e) => { setClubId(e.target.value); setGameId(''); setPlan(null); setRefusal(null) }}>
            <option value="" disabled>
              {games === null ? 'Loading…' : clubs.length ? 'Pick a club' : 'No games to re-derive'}
            </option>
            {clubs.map((c) => (
              <option key={c.org_id} value={String(c.org_id)}>{c.org_name}</option>
            ))}
          </select>
        </label>
        <label className="adm-field">
          <span className="t-label">Match</span>
          <select className="adm-select" value={gameId} aria-label="Match"
                  disabled={!clubId}
                  onChange={(e) => { setGameId(e.target.value); setPlan(null); setRefusal(null) }}>
            <option value="" disabled>Pick a match</option>
            {matches.map((g) => (
              <option key={g.game_id} value={String(g.game_id)}>
                {g.game_code}{g.match_date ? ` · ${g.match_date}` : ''} · #{g.game_id}
              </option>
            ))}
          </select>
        </label>
        <button className="adm-btn adm-btn--quiet ops-btn"
                disabled={busy || !gameId} onClick={check}>Check</button>
      </div>
      {refusal && <p className="ops-err">{refusal}</p>}
      {plan && (
        <table className="adm-table">
          <thead><tr><th>Game</th><th>Job re-derived</th>
            <th>Kickoff</th><th>Halftime</th><th>Full time</th><th /></tr></thead>
          <tbody>
            <tr>
              <td className="adm-primary">{plan.game_code}</td>
              <td className="t-data">{plan.job_id.slice(0, 8)}</td>
              <td className="t-data">{mmss(plan.game_timing.kickoff_s)}</td>
              <td className="t-data">
                {mmss(plan.game_timing.h1_end_s)}–{mmss(plan.game_timing.h2_start_s)}
              </td>
              <td className="t-data">{mmss(plan.game_timing.game_end_s)}</td>
              <td className="ops-actions">
                <button className="adm-btn adm-btn--danger ops-btn" disabled={busy}
                        onClick={start}>Re-derive</button>
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </section>
  )
}

export default function Operations({ session }: { session: Session }) {
  const [data, setData] = useState<Payload | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ ok: boolean; text: string } | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null)
  const [health, setHealth] = useState<Health | null>(null)
  const [uploads, setUploads] = useState<Upload[] | null>(null)
  const timer = useRef<number | null>(null)

  const mayAct = session.user.is_platform_admin

  const load = useCallback(() => {
    api<Payload>('/api/v1/ops/jobs')
      .then((d) => { setData(d); setFetchedAt(new Date()); setError(null) })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : String(e)))
    // Secondary panels never take the page down with them: at 2am the job
    // tables are what is being read, and null here renders as "unavailable"
    // rather than as an empty list, which would read as "nothing to see".
    api<{ uploads: Upload[] }>('/api/v1/ops/uploads')
      .then((d) => setUploads(d.uploads))
      .catch(() => setUploads(null))
  }, [])

  useEffect(() => {
    load()
    timer.current = window.setInterval(load, REFRESH_MS)
    return () => { if (timer.current) window.clearInterval(timer.current) }
  }, [load])

  // Configuration cannot change under a running revision, so this is read
  // once rather than on the refresh cycle.
  useEffect(() => {
    api<Health>('/api/v1/ops/health')
      .then(setHealth)
      .catch(() => setHealth(null))
  }, [])

  const act = async (job: Job, action: string) => {
    if (!window.confirm(CONFIRM[action]?.(job) ?? `${action} ${job.game_code}?`)) return
    setBusy(`${job.id}:${action}`)
    try {
      const r = await api<{ success?: boolean; unconfirmed_pods?: string[] }>(
        `/api/v1/ops/jobs/${job.id}/${action}`, { method: 'POST' })
      // Fail promises to kill the job's pods: say so when RunPod did not
      // confirm some of them gone, instead of a plain "done".
      const left = r?.unconfirmed_pods ?? []
      setToast(left.length
        ? { ok: false, text: `${action}: job failed, but ${left.length} pod`
            + `${left.length > 1 ? 's were' : ' was'} not confirmed gone `
            + `(${left.join(', ')}) — see the RunPod pods table` }
        : { ok: true, text: `${action}: done` })
      load()
    } catch (e) {
      setToast({ ok: false, text: e instanceof ApiError ? e.message : String(e) })
    } finally {
      setBusy(null)
    }
  }

  if (error) {
    return (<><h1 className="page-title">Operations</h1><p className="error">{error}</p></>)
  }
  if (!data) return <p className="muted">Loading…</p>

  const review = data.active.filter((j) => j.status === 'calibration_review')
  const active = data.active.filter((j) => j.status !== 'calibration_review')

  const Btn = ({ job, action, label, danger }: {
    job: Job; action: string; label: string; danger?: boolean
  }) => (
    <button
      className={`adm-btn adm-btn--${danger ? 'danger' : 'quiet'} ops-btn`}
      disabled={busy === `${job.id}:${action}`}
      onClick={() => act(job, action)}
    >{label}</button>
  )

  /* Configuration, rendered once and PLACED by urgency: above the job
     tables when something required is missing — a bad webhook URL means
     every job finishes silently, which outranks any single stuck pod — and
     at the foot of the page when it is all fine. */
  const config = health && (
    <>
      <h2 className="ops-h2">Pipeline configuration</h2>
      <table className="adm-table">
        <thead><tr><th>Check</th><th>State</th><th>Detail</th></tr></thead>
        <tbody>
          {health.checks.map((c) => (
            <tr key={c.name} className={c.ok ? '' : c.severity === 'advisory' ? 'ops-warn' : 'ops-bad'}>
              <td className="adm-primary">{c.name}</td>
              <td className={`ops-check ops-check--${c.ok ? 'ok' : c.severity}`}>
                {c.ok ? 'OK' : c.severity === 'advisory' ? 'OPEN' : 'MISSING'}
              </td>
              <td className="t-data">{c.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )

  return (
    <>
      <h1 className="page-title">Operations</h1>
      <p className="page-sub">
        {active.length} in flight · {review.length} awaiting review ·{' '}
        {data.failed.length} recent failures. Refreshes every 30 seconds
        {fetchedAt ? `, last at ${fetchedAt.toLocaleTimeString()}` : ''}.
        {!mayAct && ' You can read this page but not act on it.'}
      </p>

      {health && !health.ok && config}

      <h2 className="ops-h2">Needs review</h2>
      <table className="adm-table">
        <thead>
          <tr><th>Game</th><th>Org</th><th className="adm-num">Waiting</th>
            <th>Calibration</th><th /></tr>
        </thead>
        <tbody>
          {review.map((j) => (
            <tr key={j.id}>
              <td className="adm-primary">{j.game_code}</td>
              <td className="t-data">{j.org_id}</td>
              <td className="adm-num">{elapsed(j.stage_elapsed_s)}</td>
              <td>
                {j.calibration ? (
                  <>
                    <span className="t-data">n={j.calibration.n_samples}</span>{' '}
                    <span className={`ops-verdict ops-verdict--${j.calibration.verdict}`}>
                      {j.calibration.verdict}
                    </span>
                  </>
                ) : <span className="muted">no precheck</span>}
              </td>
              <td className="ops-actions">
                {j.review_url && (
                  <a className="adm-btn adm-btn--quiet ops-btn" href={j.review_url}>
                    Open review
                  </a>
                )}
                {mayAct && <Btn job={j} action="fail" label="Fail" danger />}
              </td>
            </tr>
          ))}
          {review.length === 0 && (
            <tr><td colSpan={5} className="adm-empty">Nothing waiting on a person.</td></tr>
          )}
        </tbody>
      </table>

      <h2 className="ops-h2">In flight</h2>
      <table className="adm-table">
        <thead>
          <tr><th>Game</th><th>Org</th><th>Uploader</th><th>Status</th>
            <th>Stage</th><th className="adm-num">In stage</th><th>Pod</th><th /></tr>
        </thead>
        <tbody>
          {active.map((j) => (
            <tr key={j.id} className={`ops-${j.alert}`}>
              <td className="adm-primary">{j.game_code}</td>
              <td className="t-data">{j.org_id}</td>
              <td>
                {j.uploader ? (
                  <>
                    {j.uploader.split('@')[0]}
                    {j.external && <span className="adm-chip adm-chip--impersonate">EXT</span>}
                  </>
                ) : <span className="muted">—</span>}
              </td>
              <td className="t-data">{j.status}</td>
              <td>
                <span className="ops-stage">{j.stage}</span>
                {j.stage_detail && <span className="muted"> {j.stage_detail}</span>}
              </td>
              <td className={`adm-num ops-elapsed ops-elapsed--${j.alert}`}>
                {elapsed(j.stage_elapsed_s)}
              </td>
              <td className="t-data muted">{j.pod_id ?? '—'}</td>
              <td className="ops-actions">
                {mayAct && ['pod_launching', 'processing'].includes(j.status)
                  && !j.stage.startsWith('chunks') && !j.stage.startsWith('finalize')
                  && <Btn job={j} action="relaunch" label="Relaunch" />}
                {j.game_code && (j.stage.startsWith('finalize') || j.stage.startsWith('chunks')) && (
                  <a className="adm-btn adm-btn--quiet ops-btn"
                     href={`/match-room/${pathSeg(j.game_code)}`}>Game</a>
                )}
                {mayAct && <Btn job={j} action="fail" label="Fail" danger />}
              </td>
            </tr>
          ))}
          {active.length === 0 && (
            <tr><td colSpan={8} className="adm-empty">Nothing in flight.</td></tr>
          )}
        </tbody>
      </table>

      <h2 className="ops-h2">Recent failures</h2>
      <table className="adm-table">
        <thead><tr><th>Game</th><th>Org</th><th>Error</th><th /></tr></thead>
        <tbody>
          {data.failed.map((j) => (
            <tr key={j.id}>
              <td className="adm-primary">{j.game_code}</td>
              <td className="t-data">{j.org_id}</td>
              <td className="ops-err">{j.error}</td>
              <td className="ops-actions">
                {mayAct && <>
                  <Btn job={j} action="requeue" label="Requeue" />
                  <Btn job={j} action="retry-chunks" label="Retry chunks" />
                  <Btn job={j} action="resurrect-finalize" label="Resurrect finalize" />
                </>}
              </td>
            </tr>
          ))}
          {data.failed.length === 0 && (
            <tr><td colSpan={4} className="adm-empty">No recent failures.</td></tr>
          )}
        </tbody>
      </table>

      {mayAct && <RerunFinalize onDone={(t) => { setToast(t); load() }} />}
      {mayAct && <RerunRederive onDone={(t) => { setToast(t); load() }} />}

      <RunPodPods mayAct={mayAct} />

      <h2 className="ops-h2">Recent uploads</h2>
      <p className="page-sub">
        Every door in: the upload page, the SDK tracking intake and the lead
        portal. A job that died before dispatch appears here and nowhere above.
      </p>
      <table className="adm-table">
        <thead>
          <tr><th>Source</th><th>Game</th><th>Org</th><th>Status</th><th>File</th>
            <th className="adm-num">Size</th><th>When</th><th>Error</th><th /></tr>
        </thead>
        <tbody>
          {(uploads ?? []).map((u) => (
            <tr key={`${u.source}:${u.id}`}
                className={u.error || u.status === 'failed' ? 'ops-bad'
                  : ['pending', 'uploading', 'queued'].includes(u.status ?? '') ? 'ops-warn' : ''}>
              <td><span className="ops-stage">{u.source}</span></td>
              <td className="adm-primary">{u.game_code ?? '—'}</td>
              <td className="t-data">{u.org ?? '—'}</td>
              <td className="t-data">{u.status ?? '—'}</td>
              <td className="t-data">{u.filename ?? '—'}</td>
              <td className="adm-num">{u.size_mb === null ? '—' : `${u.size_mb} MB`}</td>
              <td className="t-data muted">
                {u.uploaded_at ? new Date(u.uploaded_at).toLocaleString() : '—'}
              </td>
              <td className="ops-err">{u.error}</td>
              <td className="ops-actions">
                {u.calibration_url && (
                  <a className="adm-btn adm-btn--quiet ops-btn" href={u.calibration_url}>
                    Open review
                  </a>
                )}
              </td>
            </tr>
          ))}
          {uploads !== null && uploads.length === 0 && (
            <tr><td colSpan={9} className="adm-empty">No recent uploads.</td></tr>
          )}
          {uploads === null && (
            <tr><td colSpan={9} className="adm-empty">Recent uploads unavailable.</td></tr>
          )}
        </tbody>
      </table>

      {health && health.ok && config}

      {toast && (
        <div className={`ops-toast${toast.ok ? '' : ' is-error'}`}
             onAnimationEnd={() => setToast(null)}>{toast.text}</div>
      )}
    </>
  )
}
